import { useCallback, useEffect, useSyncExternalStore } from "react";
import { onForeground } from "@/lib/foreground";

import {
  actOnOtaInstruction,
  dismissUpdatePrompt,
  fetchReleaseStatus,
  gateVerdict,
  type GateVerdict,
} from "@/lib/release-status";

/**
 * Verdict state lives OUTSIDE React (same module-level idiom as the access
 * token in api/client.ts): the effect below only starts network work, and the
 * store notifies React when an answer lands — never a setState inside an
 * effect body.
 */
const NO_VERDICT: GateVerdict = { blocked: false, prompt: false };
let currentVerdict: GateVerdict = NO_VERDICT;
const listeners = new Set<() => void>();

function publishVerdict(next: GateVerdict): void {
  // Referential stability: useSyncExternalStore compares snapshots by
  // identity, and a fresh-but-equal object every refresh would re-render the
  // root on each foreground for nothing.
  if (
    next.blocked === currentVerdict.blocked &&
    next.prompt === currentVerdict.prompt
  ) {
    return;
  }
  currentVerdict = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getVerdict(): GateVerdict {
  return currentVerdict;
}

async function refresh(): Promise<void> {
  const status = await fetchReleaseStatus();
  actOnOtaInstruction(status?.ota ?? null);
  publishVerdict(await gateVerdict(status));
}

/**
 * The launch trigger, mounted at the root (ADR-029): asks the server on
 * launch and on each foreground, hands any OTA instruction to the (Stage 1:
 * no-op) trigger, and turns the gate answer into UI state.
 *
 * Fails open by construction: the initial state shows nothing, and a failed
 * fetch leaves it that way. The one deliberate asymmetry: a verdict can only
 * BLOCK after a successful, validated server answer — never from an error.
 */
export function useUpdateGate(): GateVerdict & { dismiss: () => void } {
  const verdict = useSyncExternalStore(subscribe, getVerdict, getVerdict);

  useEffect(() => {
    void refresh();
    // On resume too (ADR-028: "on launch and on resume") — a session left
    // open overnight must still learn its build was retired. Rate limited,
    // because on web a resume is a tab flip: the server's 60s cache made the
    // ANSWER cheap, but the request itself was still made every time.
    return onForeground(
      "release.gate",
      () => {
        void refresh();
      },
      // The launch fetch immediately above IS this job's first run.
      { justRan: true },
    );
  }, []);

  const dismiss = useCallback(() => {
    publishVerdict({ ...currentVerdict, prompt: false });
    void dismissUpdatePrompt();
  }, []);

  return { ...verdict, dismiss };
}
